import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import faqData from '../data/faqData.json';
import OtherCalcs from 'components/OtherCalcs';
import { getFaqIcon } from '../utils/faqUtils';
import TitleHelmet from 'components/TitleHelmet';

interface FAQItem {
  id: string;
  question: string;
  answer?: string;
  filePath?: string;
  linkURL?: string;
  hidden?: boolean;
}

const FAQList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const location = useLocation();

  // Handle search parameter from URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const searchFromUrl = searchParams.get('search');
    if (searchFromUrl) {
      setSearchTerm(searchFromUrl);
    }
  }, [location.search]);

  const visibleFaqs = useMemo(() => {
    return (faqData as FAQItem[]).filter(item => !item.hidden);
  }, []);

  const filteredFaqs = useMemo(() => {
    if (!searchTerm.trim()) return visibleFaqs;

    return visibleFaqs.filter(item => item.question.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [visibleFaqs, searchTerm]);

  return (
    <div className="faq-list-page">
      <TitleHelmet
        title="Frequently Asked Questions, or everything you need to know about GLP-1s"
        description="Find answers to the
        most common questions about GLP-1 medications like semaglutide and tirzepatide. Learn about dosage, side
        effects, compounding, and more from our comprehensive FAQ."
      />
      <div className="faq-list-header">
        <h1 className="faq-main-title">Frequently Asked Questions</h1>
        <p className="faq-subtitle">
          We see a lot of questions about semaglutide and tirzepatide on Reddit, and, tbh, most of them come up again
          and again. So here are some of the most common questions we've seen, and some answers.
        </p>

        <div className="faq-search-container">
          <div className="search-input-wrapper">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search questions..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="faq-search-input"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="search-clear-btn" aria-label="Clear search">
                ✕
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="faq-results-info">
        {searchTerm ? (
          <p className="faq-results-text">
            Found {filteredFaqs.length} question{filteredFaqs.length !== 1 ? 's' : ''}
            {filteredFaqs.length > 0 && <span> matching "{searchTerm}"</span>}
          </p>
        ) : (
          <p className="faq-results-text">
            {visibleFaqs.length} question{visibleFaqs.length !== 1 ? 's' : ''} available
          </p>
        )}
      </div>

      {filteredFaqs.length === 0 ? (
        <div className="faq-no-results">
          <span className="no-results-icon">🤷</span>
          <h3>No questions found</h3>
          <p>
            Try adjusting your search or{' '}
            <button onClick={() => setSearchTerm('')} className="link-button">
              browse all questions
            </button>
          </p>
        </div>
      ) : (
        <div className="faq-grid">
          {filteredFaqs.map((item, index) => (
            <a
              key={item.id}
              href={item.linkURL ? item.linkURL : `/faq/${item.id}`}
              className="faq-card"
              style={{ '--card-delay': `${index * 0.1}s` } as React.CSSProperties}
            >
              <div className="faq-card-header">
                <span className="faq-card-number">#{index + 1}</span>
              </div>
              <div className="faq-card-content">
                <span className="faq-card-icon">{getFaqIcon(item.question)}</span>
                <h3 className="faq-card-question">{item.question}</h3>
              </div>
              {/* <div className="faq-card-footer">
                <span className="faq-card-arrow">→</span>
              </div> */}
            </a>
          ))}
        </div>
      )}

      <div className="faq-help-section">
        <div className="faq-help-card">
          <h3>
            <span className="help-icon">👩🏻‍⚕️</span>
            Still have questions?
            <span className="help-icon">👨🏽‍⚕️</span>
          </h3>
          <p>
            As always, when in doubt, consult with your healthcare provider. These FAQs are based on personal
            experiences, and layperson research, and should not be considered medical advice.
          </p>
        </div>
      </div>

      <OtherCalcs sections="calcs" />
    </div>
  );
};

export default FAQList;
